import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthShell, { GoogleButton } from "@/components/AuthShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "mt-1.5 w-full h-11 px-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none";

export default function Register() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "", country: "US", account_type: "business" });
  const [consents, setConsents] = useState({ tos: false, privacy: false, esign: false, compliance: false });
  const [loading, setLoading] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (!/[A-Za-z]/.test(f.password) || !/\d/.test(f.password)) { toast.error("Password must include a letter and a number"); return; }
    if (!consents.tos || !consents.privacy || !consents.esign || !consents.compliance) {
      toast.error("Please accept all required agreements to continue"); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        name: f.name, email: f.email, password: f.password, phone: f.phone,
        country: f.country, account_type: f.account_type,
        accept_tos: consents.tos, accept_privacy: consents.privacy,
        accept_esign: consents.esign, accept_compliance: consents.compliance,
      });
      setUser(data);
      toast.success("Account created! Let's verify your email.");
      navigate("/onboarding");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const Consent = ({ k, children }) => (
    <label className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed">
      <input type="checkbox" data-testid={`consent-${k}`} checked={consents[k]} onChange={(e) => setConsents({ ...consents, [k]: e.target.checked })} className="mt-0.5" />
      <span>{children}</span>
    </label>
  );

  return (
    <AuthShell
      title="Create your account"
      subtitle="Join the AI-powered business opportunity ecosystem."
      footer={<>Already have an account? <Link to="/login" className="text-[#2563EB] font-medium" data-testid="link-login">Sign in</Link></>}
    >
      <form onSubmit={submit} className="space-y-3.5">
        <div>
          <label className="text-sm font-medium text-slate-700">Full legal name</label>
          <input required value={f.name} onChange={up("name")} data-testid="register-name-input" className={inputCls} placeholder="Jordan Rivera" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Work email</label>
          <input type="email" required value={f.email} onChange={up("email")} data-testid="register-email-input" className={inputCls} placeholder="you@company.com" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-slate-700">Phone</label>
            <input value={f.phone} onChange={up("phone")} data-testid="register-phone-input" className={inputCls} placeholder="+1 415 555 1212" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Country</label>
            <input value={f.country} onChange={up("country")} data-testid="register-country-input" className={inputCls} placeholder="US" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Account type</label>
          <select value={f.account_type} onChange={up("account_type")} data-testid="register-account-type" className={inputCls}>
            {["business", "investor", "supplier", "contractor", "provider", "buyer", "lender"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Password</label>
          <input type="password" required value={f.password} onChange={up("password")} data-testid="register-password-input" className={inputCls} placeholder="8+ chars, a letter and a number" />
        </div>
        <div className="space-y-2 pt-1">
          <Consent k="tos">I accept the <span className="text-[#2563EB]">Terms of Service</span>.</Consent>
          <Consent k="privacy">I consent to the <span className="text-[#2563EB]">Privacy Policy</span>.</Consent>
          <Consent k="esign">I consent to electronic records and electronic signatures.</Consent>
          <Consent k="compliance">I consent to identity, business and compliance verification checks.</Consent>
        </div>
        <button type="submit" disabled={loading} data-testid="register-submit-button"
          className="w-full h-11 rounded-md bg-[#2563EB] text-white font-medium hover:bg-[#1D4ED8] transition-colors flex items-center justify-center disabled:opacity-60">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Create account"}
        </button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
        <div className="h-px bg-slate-200 flex-1" /> OR <div className="h-px bg-slate-200 flex-1" />
      </div>
      <GoogleButton label="Sign up with Google" />
    </AuthShell>
  );
}
